import React, { useState } from 'react';
import { Disclosure, Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useLocation } from 'react-router-dom';
import Logo from '../atoms/Logo';
import NavLink from '../molecules/NavLink';
import MobileNavLink from '../molecules/MobileNavLink';
import Container from '../atoms/Container';
import LanguageSwitcher from '../atoms/LanguageSwitcher';
import LocalizedLink from '../atoms/LocalizedLink';
import { useTranslation } from 'react-i18next';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  
  // Main navigation structure with dropdowns
  const navigation = [
    { name: t('nav.home'), href: '/' },
    { 
      name: t('nav.about'), 
      href: '/about',
      children: [
        { name: t('nav.dropdown.aboutUs'), href: '/about' },
        { name: t('nav.dropdown.accreditation'), href: '/accreditation' },
        { name: t('nav.dropdown.facultyStaff'), href: '/faculty' },
        { name: t('nav.dropdown.newsEvents'), href: '/news' },
        { name: t('nav.dropdown.alumni'), href: '/alumni' },
        { name: t('nav.dropdown.employment'), href: '/employment' },
        { name: t('nav.admissions'), href: '/admissions' },
      ]
    },
    { 
      name: t('nav.academics'), 
      href: '/academics',
      children: [
        { name: t('nav.dropdown.academicPrograms'), href: '/academics' },
        { name: t('nav.dropdown.curriculum'), href: '/curriculum' },
        { name: t('nav.dropdown.preschool'), href: '/preschool' },
        { name: t('nav.dropdown.elementarySchool'), href: '/elementary-school' },
        { name: t('nav.dropdown.middleSchool'), href: '/middle-school' },
        { name: t('nav.dropdown.highSchool'), href: '/high-school' },
        { name: t('nav.dropdown.learningSupport'), href: '/learning-support' },
        { name: t('nav.dropdown.universityCounseling'), href: '/university-counseling' },
      ]
    },
    { 
      name: t('nav.studentLife'), 
      href: '/activities',
      children: [
        { name: t('nav.dropdown.schoolCalendar'), href: '/calendar' },
        { name: t('nav.dropdown.communityService'), href: '/community-service' },
        { name: t('nav.dropdown.studentResources'), href: '/resources' },
      ]
    },
    { name: t('nav.faq'), href: '/faq' },
    { name: t('nav.contact'), href: '/contact' },
  ];

  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  return (
    <Disclosure as="nav" className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-100 backdrop-blur-sm bg-white/95">
      {({ open }) => (
        <>
          <Container className="px-4 sm:px-6 lg:px-8">
            <div className="flex h-20 justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0 py-2">
                  <Logo />
                </div>
                <div className="hidden md:flex md:ml-12 lg:ml-16 md:space-x-1 lg:space-x-2 items-center">
                  {navigation.map((item) => (
                    <div 
                        key={item.href}
                      className="relative h-full flex items-center" 
                      onMouseEnter={() => item.children ? setHoveredItem(item.name) : null}
                      onMouseLeave={() => item.children ? setHoveredItem(null) : null}
                    >
                      <LocalizedLink 
                        to={item.href}
                        className={`
                          inline-flex items-center px-4 py-2 text-base font-medium border-b-2 transition-all duration-300
                          ${hoveredItem === item.name || (location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(item.href)))
                            ? 'border-tlc-gold text-tlc-navy' 
                            : 'border-transparent text-gray-600 hover:text-tlc-navy hover:border-tlc-gold/50'}
                        `}
                      >
                        {item.name}
                      </LocalizedLink>

                      {item.children && (
                        <Transition
                          show={hoveredItem === item.name}
                          enter="transition ease-out duration-200"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-150"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <div className="absolute left-0 z-10 mt-2 w-80 origin-top-left rounded-lg bg-white shadow-xl ring-1 ring-black/5 focus:outline-none top-full overflow-hidden border border-gray-100">
                            <div className="pt-2 pb-2">
                              <div className="h-1 w-12 bg-tlc-gold/80 rounded-full mx-4 mb-2"></div>
                              <div className="grid grid-cols-2 gap-1">
                                {item.children.map((child: any) => (
                                  <NavLink
                                    key={child.href}
                                    to={child.href}
                                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-tlc-navy transition-all duration-200 border-l-2 border-transparent hover:border-tlc-gold hover:pl-5"
                                  >
                                    {child.name}
                      </NavLink>
                                ))}
                              </div>
                            </div>
                          </div>
                        </Transition>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="hidden md:flex md:items-center">
                <LanguageSwitcher />
              </div>
              <div className="-mr-2 flex items-center md:hidden">
                <Disclosure.Button className="inline-flex items-center justify-center rounded-full p-2 text-gray-500 hover:bg-tlc-navy/10 hover:text-tlc-navy focus:outline-none focus:ring-2 focus:ring-inset focus:ring-tlc-gold transition-colors duration-200">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>
            </div>
          </Container>

          <Disclosure.Panel className="md:hidden">
            <div className="space-y-1 pt-2 pb-3 max-h-[70vh] overflow-y-auto bg-white/95 backdrop-blur-sm">
              {navigation.map((item) => (
                item.children ? (
                  <MobileDropdown key={item.href} item={item} />
                ) : (
                  <MobileNavLink
                    key={item.href}
                    to={item.href}
                    exact={item.href === '/'}
                    className="block text-base font-medium text-gray-700 hover:bg-tlc-navy/5 hover:text-tlc-navy transition-colors duration-200 px-4 py-3"
                  >
                    {item.name}
                  </MobileNavLink>
                )
              ))}
            </div>
            <div className="border-t border-gray-100 pt-4 pb-3 bg-white/95 backdrop-blur-sm">
              <div className="flex items-center justify-around">
                <LanguageSwitcher />
              </div>
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
};

// MobileDropdown component for mobile navigation
const MobileDropdown: React.FC<{ item: any }> = ({ item }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <div className="flex items-center bg-white">
        <MobileNavLink
          to={item.href}
          className="flex-1 px-4 py-3 text-base font-medium text-gray-700 hover:bg-tlc-navy/5 hover:text-tlc-navy transition-colors duration-200"
        >
          {item.name}
        </MobileNavLink>
        <button
          className={`
            p-2 mx-2 rounded-full text-base font-medium transition-colors duration-200
            ${isOpen
              ? 'bg-tlc-navy/5 text-tlc-navy'
              : 'text-gray-700 hover:bg-gray-50 hover:text-tlc-navy'}
          `}
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
        >
          {isOpen ? "−" : "+"}
        </button>
      </div>

      <Transition
        show={isOpen}
        enter="transition ease-out duration-200"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-150"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <div className="bg-gray-50/70 pl-4 pr-2 py-2 space-y-1">
          <div className="h-1 w-8 bg-tlc-gold/60 rounded-full mb-2"></div>
          {item.children.map((child: any) => (
            <MobileNavLink
              key={child.href}
              to={child.href}
              className="pl-4 py-2 block border-l-2 border-gray-200 text-gray-600 hover:border-tlc-gold hover:text-tlc-navy hover:bg-white/80 transition-all duration-200 rounded-r-md hover:pl-5"
            >
              {child.name}
            </MobileNavLink>
          ))}
        </div>
      </Transition>
    </div>
  );
};

export default Header; 